import { PoweroffOutlined } from "@ant-design/icons";
import React, { useCallback, useMemo } from 'react';
import { connect, useDispatch } from "react-redux";
import { NavLink } from "react-router-dom";
import { loginActions } from "../../../pages/LoginPage/reducer";
import { AppState } from "../../../util/store/store";
import { PATHNAME } from "../../../util/varibles/constants";
import { ACTION } from "../../../util/varibles/permission";
import Icon, { ICON_NAME } from "../../Icon";
import Menu from "../../Menu";
import MessageBadge from "./MessageBadge";
import Notification from "./Noticaition";
import SearchGlobal from "./SearchGlobal";
import styles from './style.module.scss';
import ThemeColor from "./ThemeColor";
import ViewMode from "./ViewMode";

const mapStateToProps = (state: AppState) => {
    const { username = '', email = '', permission = {} } = state.login.user || {};
    return {
        username,
        email,
        permission,
    }
};

interface IProps {
    username: string
    email: string
    permission: any
}

type IconSize = 'small' | 'normal' | 'large' | number;

interface ISettingMenuItem {
    permission: string
    key: string
    to: string
    icon: ICON_NAME
    iconSize: IconSize
    iconClassName?: string
    label: string
}

const MAIN_NAV_ITEMS = [
    { permission: ACTION.DASHBOARD.VIEW, title: 'Dashboard', to: PATHNAME.HOME, icon: ICON_NAME.DASHBOARD, label: 'Dashboard' },
    { permission: ACTION.PLAN_OP.VIEW, title: 'Plan Operation', to: PATHNAME.PLAN, icon: ICON_NAME.PLAN, label: 'Plan Operation', iconClassName: "mt-1 min-w-[20px]" },
    { permission: ACTION.OVERVIEW.VIEW, title: 'Operation Overview', to: PATHNAME.OVERVIEW, icon: ICON_NAME.OVERVIEW, label: 'Operation Overview' },
    { permission: ACTION.VESSEL_OVERVIEW.VIEW, title: 'Vessel Overview', to: PATHNAME.VESSEL_OVERVIEW, icon: ICON_NAME.VESSEL, label: 'Vessel Overview' },
    { permission: ACTION.BIOLOGY_OVERVIEW.VIEW, title: 'Site overview', to: PATHNAME.SITE_OVERVIEW, icon: ICON_NAME.CIRCLE, label: 'Biology overview' },
];

const SETTING_SECTION_ITEMS: ISettingMenuItem[] = [
    { permission: ACTION.CHECKLIST.VIEW, key: 'checklist', to: PATHNAME.CHECKLIST, icon: ICON_NAME.CHECKLIST, iconSize: 'large', label: 'Checklist' },
    { permission: ACTION.OPERATION_TASK_BUILDER.VIEW, key: 'operation-task-builder', to: PATHNAME.OPERATION_TASK_BUILDER, icon: ICON_NAME.ADD, iconSize: 'normal', iconClassName: 'ml-1', label: 'Operation, task builder' },
    { permission: ACTION.OP_SETTING.VIEW, key: 'op-setting', to: PATHNAME.OPERATION_SETTING, icon: ICON_NAME.OPERATION_SETTING, iconSize: 'large', label: 'Operation' },
    { permission: ACTION.ROUTE.VIEW, key: 'route', to: PATHNAME.ROUTE, icon: ICON_NAME.ROUTE, iconSize: 'large', label: 'Route' },
    { permission: ACTION.VESSEL.VIEW, key: 'vessel', to: PATHNAME.VESSEL, icon: ICON_NAME.VESSEL, iconSize: 'large', iconClassName: 'ml-1', label: 'Vessel' },
    { permission: ACTION.LOCATION.VIEW, key: 'location', to: PATHNAME.LOCATION, icon: ICON_NAME.LOCATION, iconSize: 'large', iconClassName: 'ml-1', label: 'Location' },
    { permission: ACTION.DISEASE.VIEW, key: 'disease', to: PATHNAME.DISEASE, icon: ICON_NAME.DISEASE, iconSize: 20, label: 'Disease' },
    { permission: ACTION.WEATHER.VIEW, key: 'weather', to: PATHNAME.WEATHER, icon: ICON_NAME.WEATHER, iconSize: 'large', label: 'Weather' },
    { permission: ACTION.USER.VIEW, key: 'user', to: PATHNAME.USER, icon: ICON_NAME.USER, iconSize: 'large', iconClassName: 'ml-1', label: 'User' },
];

const PREFERENCE_ITEMS: ISettingMenuItem[] = [
    { permission: ACTION.PREFERENCE.VIEW, key: 'preference', to: PATHNAME.PREFERENCE, icon: ICON_NAME.PREFERENCE, iconSize: 20, label: 'Preference' },
];

const getInitial = (value: string) => value.charAt(0).toUpperCase();

const buildSettingSection = (items: ISettingMenuItem[], permission: any) => {
    const menuItems = items
        .filter(item => permission[item.permission])
        .map(({ key, to, icon, iconSize, iconClassName, label }) => ({
            key,
            label: <NavLink className='menu-line' to={to}>
                <Icon className={iconClassName} icon={icon} size={iconSize} />
                <span>{label}</span>
            </NavLink>
        }));

    return menuItems.length > 0 ? [{ type: 'divider' }, ...menuItems] : [];
};

const Header: React.FC<IProps> = ({ username, email, permission }) => {
    const dispatch = useDispatch();
    const handleLogout = useCallback(() => {
        dispatch(loginActions.logout());
    }, [dispatch])

    const accountMenu = useMemo(() => [
        {
            className: styles['menu-group'],
            key: 'info-account',
            type: 'group',
            label: 'Account',
            children: [
                {
                    key: 'account',
                    className: `no-hover ${styles['menu-title']}`,
                    disabled: true,
                    label: <div className={styles.accInfo}>
                        <div className={styles.account}>
                            {getInitial(username)}
                        </div>
                        {username}
                    </div>
                }
            ],
        },
        { type: 'divider' },
        {
            className: styles['menu-group'],
            key: 'info-support',
            type: 'group',
            label: 'Ocean Planner support',
            children: [
                {
                    key: 'support',
                    className: `no-hover ${styles['menu-title']}`,
                    disabled: true,
                    label: <div className='flex flex-col'>
                        <div className={styles['line-info']}>
                            <div>Email:</div>
                            <div>support@ocean-planner.no</div>
                        </div>
                        <div className={styles['line-info']}>
                            <div>Phone:</div>
                            <div>+47 455 00 924</div>
                        </div>
                    </div>
                }
            ],
        },
        { type: 'divider' },
        {
            key: 'logout',
            label: <div className='menu-line' onMouseDown={handleLogout}>
                <PoweroffOutlined className={styles.logout} />
                <span>Logout</span>
            </div>
        }
    ], [handleLogout, username]);

    const settingMenu = useMemo(() => [
        {
            key: 'title',
            className: `no-hover ${styles['menu-title']}`,
            disabled: true,
            label: <span className={styles.titleMenu}>Setting & Management</span>
        },
        ...buildSettingSection(SETTING_SECTION_ITEMS, permission),
        ...buildSettingSection(PREFERENCE_ITEMS, permission)
    ], [permission])

    return <div className={styles.header}>
        <div className={styles.headerLeft}>
            <div className={styles.logo} />
            {MAIN_NAV_ITEMS
                .filter(item => permission[item.permission])
                .map(item => <NavLink key={item.to} title={item.title} to={item.to} className={styles.tab}>
                    <Icon className={item.iconClassName} icon={item.icon} size='large' />
                    <span data-lev='name'>{item.label}</span>
                </NavLink>)}
            {permission[ACTION.MESSENGER.VIEW] &&
                <NavLink title='Messaging' to={PATHNAME.MESSAGE} className={styles.tab}>
                    <MessageBadge />
                </NavLink>}
        </div>
        <div className={styles.headerRight}>
            {permission[ACTION.GLOBAL.SEARCH] && <SearchGlobal />}
            <ViewMode />
            <ThemeColor />
            <Notification />
            {permission[ACTION.MESSENGER.VIEW] && <Menu items={settingMenu}>
                <div className={styles['action-small']} title='Setting'>
                    <Icon className={styles.setting} icon={ICON_NAME.SETTING} size={20} />
                </div>
            </Menu>}

            <Menu items={accountMenu}>
                <div className={styles['action-small']} title='Account'>
                    <div className={styles.account}>
                        {getInitial(email)}
                    </div>
                </div>
            </Menu>
        </div>
    </div>;
};

export default connect(mapStateToProps, {})(Header);
